import React, { useMemo, useState } from 'react';
import './SkinMacroCloseLooks.css';

const REGION_ORDER = ['forehead', 'cheek'];

function regionLabel(id) {
  if (id === 'forehead') return 'Forehead';
  if (id === 'cheek' || id === 'left_cheek' || id === 'right_cheek') return 'Cheek';
  return String(id || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function severityClass(level) {
  const n = Number(level) || 0;
  if (n >= 3) return 'is-high';
  if (n >= 2) return 'is-moderate';
  return 'is-mild';
}

function RegionNotes({ active }) {
  const conditions = Array.isArray(active.conditions)
    ? active.conditions
    : (Array.isArray(active.analysis?.conditions) ? active.analysis.conditions : []);
  const summary =
    active.visual_summary
    || active.analysis?.visual_summary
    || 'Close-up captured for salon consultation.';

  return (
    <div className="skin-macro-close__notes">
      <h3>What we see</h3>
      <p>{summary}</p>
      {conditions.length > 0 ? (
        <ul className="skin-macro-close__chips">
          {conditions.slice(0, 6).map((c) => {
            const name = String(c.name || '').replace(/_/g, ' ');
            const sev = String(c.severity || 'mild');
            return (
              <li key={`${c.name}-${sev}`} className={severityClass(c.severity_level)}>
                <span className="skin-macro-close__chip-name">{name}</span>
                <span className="skin-macro-close__chip-sev">{sev}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="skin-macro-close__muted">No strong close-up concerns flagged for this frame.</p>
      )}
      {Array.isArray(active.legend) && active.legend.length > 0 ? (
        <div className="skin-macro-close__legend">
          {active.legend.slice(0, 6).map((item) => (
            <span key={item.label || item.type} className="skin-macro-close__legend-item">
              <i style={{ background: item.color || '#0f766e' }} aria-hidden />
              {item.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * USB microscope close-looks: original / OpenCV overlay + Qwen notes per region.
 * pdfMode expands all regions with original + highlights fully visible.
 */
export default function SkinMacroCloseLooks({ macro = null, pdfMode = false }) {
  const regions = useMemo(() => {
    const map = macro?.regions && typeof macro.regions === 'object' ? macro.regions : {};
    const keys = [
      ...REGION_ORDER.filter((k) => map[k]),
      ...Object.keys(map).filter((k) => !REGION_ORDER.includes(k)),
    ];
    return keys.map((id) => ({ id, ...map[id] })).filter((r) => r.original || r.overlay);
  }, [macro]);

  const [activeId, setActiveId] = useState(() => regions[0]?.id || 'forehead');
  const [viewMode, setViewMode] = useState('highlights'); // original | highlights

  const active = regions.find((r) => r.id === activeId) || regions[0] || null;
  if (!active) return null;

  if (pdfMode) {
    return (
      <div className="skin-macro-close skin-macro-close--pdf">
        {regions.map((region) => (
          <div key={region.id} className="skin-macro-close__pdf-region">
            <h3 className="skin-macro-close__pdf-heading">{regionLabel(region.id)}</h3>
            <div className="skin-macro-close__pdf-images">
              <figure className="skin-macro-close__figure">
                {region.original ? (
                  <img src={region.original} alt={`${regionLabel(region.id)} microscope original`} />
                ) : (
                  <div className="skin-macro-close__empty">No original</div>
                )}
                <figcaption>{regionLabel(region.id)} · Original capture</figcaption>
              </figure>
              <figure className="skin-macro-close__figure">
                {(region.overlay || region.original) ? (
                  <img
                    src={region.overlay || region.original}
                    alt={`${regionLabel(region.id)} microscope highlights`}
                  />
                ) : (
                  <div className="skin-macro-close__empty">No image</div>
                )}
                <figcaption>{regionLabel(region.id)} · Highlights</figcaption>
              </figure>
            </div>
            <RegionNotes active={region} />
          </div>
        ))}
      </div>
    );
  }

  const imageSrc =
    viewMode === 'original'
      ? (active.original || active.overlay)
      : (active.overlay || active.original);

  return (
    <div className="skin-macro-close">
      <div className="skin-macro-close__tabs" role="tablist" aria-label="Skin close-look regions">
        {regions.map((r) => (
          <button
            key={r.id}
            type="button"
            role="tab"
            aria-selected={active.id === r.id}
            className={`skin-macro-close__tab ${active.id === r.id ? 'is-active' : ''}`}
            onClick={() => setActiveId(r.id)}
          >
            {regionLabel(r.id)}
          </button>
        ))}
      </div>

      <div className="skin-macro-close__view-toggle" role="group" aria-label="Image view">
        <button
          type="button"
          className={viewMode === 'original' ? 'is-active' : ''}
          onClick={() => setViewMode('original')}
        >
          Original
        </button>
        <button
          type="button"
          className={viewMode === 'highlights' ? 'is-active' : ''}
          onClick={() => setViewMode('highlights')}
        >
          Highlights
        </button>
      </div>

      <div className="skin-macro-close__grid">
        <figure className="skin-macro-close__figure">
          {imageSrc ? (
            <img src={imageSrc} alt={`${regionLabel(active.id)} microscope ${viewMode}`} />
          ) : (
            <div className="skin-macro-close__empty">No image</div>
          )}
          <figcaption>{regionLabel(active.id)} · USB close-up</figcaption>
        </figure>

        <RegionNotes active={active} />
      </div>
    </div>
  );
}
